import { WizardModal } from '@/components/profile/wizard-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Link, router, usePage } from '@inertiajs/react';
import { LayoutDashboard, Menu, PlusIcon, SearchIcon, User, Venus, Mars, Transgender } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CitySwitcher } from './city-switcher';
import { LanguageSwitcher } from './language-switcher';

/** Listing paths where the search q param is applied. */
const LISTING_PATHS = new Set(['/', '/male', '/trans']);

function getListingPath(): string {
  if (typeof window === 'undefined') return '/';
  return LISTING_PATHS.has(window.location.pathname)
    ? window.location.pathname
    : '/';
}

function getInitialQ(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('q') ?? '';
}

function HeaderSearch({ onSubmit }: { onSubmit?: () => void }) {
  const { t } = useTranslation('common');
  const [q, setQ] = useState(getInitialQ);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQ(getInitialQ());
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit?.();
    router.visit(getListingPath(), {
      data: q ? { q } : {},
      preserveState: true,
      replace: true,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex-1">
      {/* Wrapper provides the relative context for the icon — not the form itself,
          which would stretch in a flex-col sheet and mis-position the absolute icon */}
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="search"
          placeholder={t('search.placeholder')}
          className="w-full pr-4 pl-9 text-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="header-search-input"
        />
      </div>
    </form>
  );
}

function MobileMenuLink({
  href,
  icon: Icon,
  children,
  onClick,
  className,
}: {
  href: string;
  icon?: React.ElementType;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted active:bg-muted ${className ?? ''}`}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
      {children}
    </Link>
  );
}

export default function Header() {
  const { t } = useTranslation('common');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { auth } = usePage().props as unknown as { auth: { user: { name: string } | null } };
  const user = auth?.user ?? null;

  function closeSheet() {
    setSheetOpen(false);
  }

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="container mx-auto flex h-14 items-center gap-3 px-4">
          {/* Logo */}
          <Link href="/" className="shrink-0" aria-label="FICKFLIX — Home">
            <img
              src="/fickflix-logo-v4.svg"
              alt="FICKFLIX"
              className="h-8 w-auto"
              draggable={false}
            />
          </Link>

          {/* Search bar — desktop */}
          <div className="hidden flex-1 items-center gap-2 md:flex">
            <CitySwitcher />
            <HeaderSearch />
          </div>

          {/* Desktop nav */}
          <nav className="hidden shrink-0 items-center gap-3 md:flex">
            <LanguageSwitcher />
            {user ? (
              <Button variant="ghost" size="sm" asChild>
                <Link href="/dashboard" className="text-muted-foreground hover:text-foreground">
                  {t('actions.dashboard', { defaultValue: 'Dashboard' })}
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" asChild>
                <Link href="/login" className="text-muted-foreground hover:text-foreground">
                  {t('actions.login')}
                </Link>
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setWizardOpen(true)}
            >
              <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
              {t('actions.add_profile')}
            </Button>
          </nav>

          {/* Mobile hamburger */}
          <div className="ml-auto flex items-center gap-2 md:hidden">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Toggle menu"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>

              <SheetContent
                side="right"
                className="flex w-80 flex-col border-border bg-card p-0"
              >
                <SheetTitle className="sr-only">{t('nav.menu')}</SheetTitle>

                {/* Search — pt-10 clears the sheet's absolute close button (top-4 right-4) */}
                <div className="border-b border-border px-4 pt-10 pb-3">
                  <HeaderSearch onSubmit={closeSheet} />
                </div>

                {/* Scrollable nav body */}
                <nav className="flex flex-1 flex-col overflow-y-auto px-2 py-3">
                  {/* Browse section */}
                  <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('nav.browse', { defaultValue: 'Browse' })}
                  </p>
                  <MobileMenuLink href="/" icon={Venus} onClick={closeSheet}>
                    {t('gender.female', { defaultValue: 'Female Escorts' })}
                  </MobileMenuLink>
                  <MobileMenuLink href="/male" icon={Mars} onClick={closeSheet}>
                    {t('gender.male', { defaultValue: 'Male Escorts' })}
                  </MobileMenuLink>
                  <MobileMenuLink href="/trans" icon={Transgender} onClick={closeSheet}>
                    {t('gender.trans', { defaultValue: 'Trans Escorts' })}
                  </MobileMenuLink>

                  <div className="my-3 border-t border-border" />

                  {/* Settings section */}
                  <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('nav.settings', { defaultValue: 'Settings' })}
                  </p>
                  <div className="px-3 py-1">
                    <CitySwitcher className="flex gap-1.5 text-muted-foreground hover:text-foreground" />
                  </div>
                  <div className="px-3 py-1">
                    <LanguageSwitcher />
                  </div>

                  <div className="my-3 border-t border-border" />

                  {/* Account section */}
                  {user ? (
                    <>
                      <MobileMenuLink href="/dashboard" icon={LayoutDashboard} onClick={closeSheet}>
                        {t('actions.dashboard', { defaultValue: 'Dashboard' })}
                      </MobileMenuLink>
                      <MobileMenuLink href="/settings/profile" icon={User} onClick={closeSheet}>
                        {t('nav.account', { defaultValue: 'Account' })}
                      </MobileMenuLink>
                    </>
                  ) : (
                    <MobileMenuLink href="/login" icon={User} onClick={closeSheet}>
                      {t('actions.login')}
                    </MobileMenuLink>
                  )}
                </nav>

                {/* Sticky CTA footer */}
                <div className="border-t border-border p-4">
                  <Button
                    size="default"
                    className="w-full"
                    onClick={() => {
                      closeSheet();
                      setWizardOpen(true);
                    }}
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    {t('actions.add_profile')}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Wizard modal — rendered outside the header so z-index stacking is clean */}
      <WizardModal open={wizardOpen} onOpenChange={setWizardOpen} />
    </>
  );
}
